<template>
  <!-- Group with appearance_aecom='table': the sub groups are the rows, the fields of the first sub group are the columns. -->
  <div v-if="showElement && status" class="my-3" :id="newElementId">

    <!-- Misconfigured table: we warn and fall back on the normal group rendering so nothing is lost. -->
    <template v-if="validationError">
      <b-alert show variant="danger" class="my-2">
        <i class="mdi mdi-alert-outline mr-1"></i>{{validationError}}
      </b-alert>
      <i-group
        :config="config"
        :formReadOnly="formReadOnly"
        :formDefinition="formDefinition"
        :value="myValue"
        @input="myValue=$event"
        :status="status"
        :surveyDoc="surveyDoc"
        :showSystemValues="showSystemValues"
        :namePath="namePath"
        :editingIndexGiven="editingIndexGiven"
        :readOnlyInput="readOnlyInput"
        >
      </i-group>
    </template>

    <template v-else>
      <div v-if="showTitle">
        <label :class="titleClass"><span v-html="label"></span></label>
        <b-btn-group v-if="$store.state.allowCopyPaste" class="ml-2">
          <b-btn @click.stop="copyValToClipboard()" size="sm"><i class="glyphicon glyphicon-copy"></i></b-btn>
          <b-btn @click.stop="getValFromClipboard()" size="sm" v-if="!readonly"><i class="glyphicon glyphicon-paste"></i></b-btn>
        </b-btn-group>
      </div>
      <div v-if="info && $store.state.form.formDisplayOptions.notePosition=='below-question'" class="text-secondary info-text mb-1">
        <i class="mdi mdi-information-outline mr-1"></i>
        <span v-html="info"></span>
      </div>

      <div class="table-responsive">
        <table class="table table-bordered table-sm aecom-input-table mb-1">
          <thead>
            <!-- First header row - only when at least one column label has a line break -->
            <tr v-if="hasTwoLevelHeader">
              <th class="corner-header" rowspan="2"></th>
              <th v-for="(cell, index) in topHeaderCells" :key="'top'+index"
                :colspan="cell.colspan" :rowspan="cell.rowspan" v-html="cell.label"></th>
            </tr>
            <tr>
              <th v-if="!hasTwoLevelHeader" class="corner-header"></th>
              <th v-for="col in subHeaderCells" :key="'sub'+col.name" v-html="col.sub"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in visibleRows" :key="row.name">
              <th scope="row" class="row-header" v-html="row.label"></th>
              <table-cell v-for="(col, colIndex) in columns" :key="row.name+'.'+col.name"
                :config="cellConfig(row, colIndex)"
                :formReadOnly="formReadOnly"
                :formDefinition="formDefinition"
                :value="cellValue(row, colIndex)"
                @input="updateValue($event, row.name, cellConfig(row, colIndex).name)"
                :status="cellStatus(row, colIndex)"
                :surveyDoc="surveyDoc"
                :showSystemValues="showSystemValues"
                :namePath="currentNamePath.concat(row.name)"
                :readOnlyInput="readOnlyInput"
                >
              </table-cell>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- The cells are too small for the usual status message, they are listed here. -->
      <ul v-if="statusMessages.length>0" class="list-unstyled small mb-0">
        <li v-for="(message, index) in statusMessages" :key="'msg'+index" :class="message.class">
          <i class="mdi mdi-alert-outline mr-1"></i>{{message.text}}
        </li>
      </ul>
    </template>

  </div>
</template>

<script>
import base from './_baseInput.js'
import formUtils from '../../utils/form_utils.js'
import tableCell from './tableCell.vue'
import cellTypes from './subInput/tableCellTypes.js'
import cuid from 'cuid'

export default {
  name:'ITableGroup',
  mixins:[base],
  components:{
    tableCell,
  },
  props:{
    editingIndexGiven:{
      type:Number,
      default:null
    }
  },
  beforeCreate(){
    // inputGroup requires all the components of this folder, so we cannot import it (circular reference).
    // It is only used for the fallback when the table is misconfigured.
    this.$options.components.IGroup = require('../inputGroup.vue').default
  },
  data(){
    return {
      newElementId:cuid(),
    }
  },
  computed:{
    rows(){
      // Every sub group is a row. Not filtered on the label as the sub groups after the first one
      // usually have no label on their fields (only the first one defines the columns).
      if(!this.config.children){
        return []
      }
      return this.config.children.filter(x=>x.hasOwnProperty('children') && x.type!='repeat').map(x=>{
        return {
          name: x.name,
          label: this.getLabel(x), // empty when the sub group has no label - the row header is then empty.
          labelText: this.getLabel(x, x.name), // to name the row in the messages
          children: x.children || [],
        }
      })
    },
    visibleRows(){
      // A row where every cell is not relevant is hidden.
      return this.rows.filter(row=>{
        const rowStatus = this.status?.[row.name]
        if(!rowStatus){
          return true
        }
        return row.children.filter(x=>rowStatus[x.name]!=4).length>0
      })
    },
    columns(){
      // The columns are defined by the fields of the first sub group.
      const first = this.rows[0]
      if(!first){
        return []
      }
      return first.children.map(x=>{
        const label = this.getLabel(x)
        const parts = label.split(/\r?\n/)
        return {
          name: x.name,
          type: x.type,
          label: label, // empty when the field has no label - the column header is then empty.
          labelFlat: this.getLabel(x, x.name).split(/\r?\n/).join(' '), // to name the column in the messages
          // 'avant\nph' => top header 'avant', sub header 'ph'. No line break => no top header.
          top: parts.length>1 ? parts[0] : null,
          sub: parts.length>1 ? parts.slice(1).join('<br>') : label,
        }
      })
    },
    hasTwoLevelHeader(){
      return this.columns.filter(col=>col.top).length>0
    },
    topHeaderCells(){
      // Consecutive columns sharing the same top label are merged with a colspan.
      const rep=[]
      this.columns.map(col=>{
        const last = rep[rep.length-1]
        if(col.top && last && last.merge && last.label==col.top){
          last.colspan++
        }else{
          rep.push({
            label: col.top || col.sub,
            merge: col.top?true:false,
            colspan: 1,
            rowspan: col.top?1:2, // a column without a top label spans the two header rows
          })
        }
      })
      return rep
    },
    subHeaderCells(){
      if(!this.hasTwoLevelHeader){
        return this.columns
      }
      return this.columns.filter(col=>col.top) // the others are already in the first row with a rowspan
    },
    validationError(){
      // All the sub groups have to define the same columns, if not we don't build the table.
      const children = this.config.children || []
      if(children.length==0){
        return 'Table "' + this.config.name + '": no sub group, a table needs one sub group per row.'
      }
      const notGroup = children.filter(x=>!x.hasOwnProperty('children') || x.type=='repeat')
      if(notGroup.length>0){
        return 'Table "' + this.config.name + '": "' + notGroup[0].name + '" is not a group. Every element of a table group has to be a group (one per row).'
      }
      if(this.columns.length==0){
        return 'Table "' + this.config.name + '": the first sub group "' + this.rows[0].name + '" has no field, it is the one defining the columns.'
      }
      const badType = this.columns.filter(col=>!cellTypes.hasOwnProperty(col.type))
      if(badType.length>0){
        return 'Table "' + this.config.name + '": the type "' + badType[0].type + '" of the field "' + badType[0].name + '" is not supported in a table. Supported types: ' + Object.keys(cellTypes).join(', ') + '.'
      }
      for(let i=1;i<this.rows.length;i++){
        const rowChildren = this.rows[i].children
        if(rowChildren.length!=this.columns.length){
          return 'Table "' + this.config.name + '": the group "' + this.rows[i].name + '" has ' + rowChildren.length + ' field(s) but the first group "' + this.rows[0].name + '" defines ' + this.columns.length + ' column(s).'
        }
        for(let j=0;j<rowChildren.length;j++){
          if(rowChildren[j].type!=this.columns[j].type){
            return 'Table "' + this.config.name + '": the field "' + rowChildren[j].name + '" of the group "' + this.rows[i].name + '" is a "' + rowChildren[j].type + '" but the column "' + this.columns[j].labelFlat + '" is a "' + this.columns[j].type + '".'
          }
        }
      }
      return null
    },
    statusMessages(){
      // The cells are too small to show the message of the status (required, constraint, flag...).
      const rep=[]
      this.visibleRows.map(row=>{
        this.columns.map((col, colIndex)=>{
          const cell = row.children[colIndex]
          const cellStatus = this.status?.[row.name]?.[cell?.name]
          const messageKey = formUtils.status?.[cellStatus]?.input_message
          if(messageKey){
            rep.push({
              text: this.stripHtml(row.labelText) + ' / ' + this.stripHtml(col.labelFlat) + ' : ' + (this.labels.status_message?.[messageKey] || messageKey),
              class: cellStatus>=20?'text-danger':'text-warning',
            })
          }
        })
      })
      return rep
    },
    showTitle(){
      if(!this.config.label){
        return false // no label on the table group => no title, like appearance_aecom='no-title'
      }
      return this.appearance_aecom.indexOf('no-title')==-1
    },
    titleClass(){
      return ['font-weight-bold', formUtils.status?.[this.groupStatus]?.label_class]
    },
  },
  methods:{
    getLabel(config, fallback=''){
      // Same logic as the label computed of _baseInput, but for any element of the definition.
      // A missing label is valid here (empty header), so the fallback is empty and not the name.
      if(!config.label){
        return fallback
      }
      if(typeof config.label === 'object'){
        let label2 = null
        const curLang = this.$store.state.language
        Object.keys(config.label).map(x=>{
          if (x.indexOf('('+curLang+')')>-1){
            label2 = config.label[x]
          }
        })
        if(!label2){
          label2 = config.label[curLang] ?? config.label.default
        }
        return label2 || fallback
      }
      return config.label
    },
    stripHtml(text){
      return (text || '').replace(/<[^>]*>/g,' ').replace(/\s+/g,' ').trim()
    },
    cellConfig(row, colIndex){
      // The fields of a row are matched by position with the columns, their name is different on each row.
      return row.children[colIndex]
    },
    cellValue(row, colIndex){
      const cell = this.cellConfig(row, colIndex)
      if(!cell || !this.myValue){
        return null
      }
      return this.myValue?.[row.name]?.[cell.name] ?? null
    },
    cellStatus(row, colIndex){
      const cell = this.cellConfig(row, colIndex)
      return this.status?.[row.name]?.[cell?.name]
    },
    updateValue(value, rowName, colName){
      if(this.myValue===null || this.myValue===undefined){
        this.myValue={}
      }
      if(this.myValue[rowName]===null || this.myValue[rowName]===undefined){
        this.$set(this.myValue, rowName, {})
      }
      this.$set(this.myValue[rowName], colName, value)
    },
  },
}
</script>

<style scoped>
.aecom-input-table th,
.aecom-input-table td{
  padding: 0.15rem 0.25rem;
  vertical-align: middle;
}
.aecom-input-table thead th{
  text-align: center;
  vertical-align: middle;
  font-size: 0.85rem;
}
.row-header{
  font-weight: normal;
  white-space: nowrap;
}
.corner-header{
  border-top: 0;
  border-left: 0;
}
.info-text{
  color: #959575;
}
</style>
